import { useCallback, useEffect, useState } from 'react'
import { Button, Input, Switch } from '@heroui/react'
import { api } from '../lib/api'
import { approvalSettingsProblem, approvalsSavable, emailProblems, MAX_APPROVAL_EMAILS, settingsToSend } from '../lib/approvalSettings'
import { requestKindLabel } from '../lib/deskStatus'
import type { ApprovalSettings, RequestKind } from '../lib/types'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Messages, useAction } from './parts'
import { AddRow, IconTile, ListCard, ListRow, ListTable, NameCell, PageHead, SaveActions } from './settingsKit'
import '../styles/people.css'

/**
 * A branch's approvals: which desk requests wait for a manager, and who is emailed
 * about them and about alarms. Saved at once, with no draft or publish.
 */

const KINDS: { kind: RequestKind; icon: IconName; tone: 1 | 2 | 3 | 4 }[] = [
  { kind: 'lunch', icon: 'clock', tone: 3 },
  { kind: 'short_break', icon: 'pause', tone: 2 },
  { kind: 'task', icon: 'document', tone: 4 },
]

const DEFAULT_SETTINGS: ApprovalSettings = { emails: [], needsApproval: KINDS.map((k) => k.kind) }

// A list the server leaves out (no addresses reads as null) is empty here.
function withDefaults(s: Partial<ApprovalSettings>): ApprovalSettings {
  return { emails: s.emails ?? [], needsApproval: s.needsApproval ?? [] }
}

export function Approvals({ branchId, organisation }: { branchId: string; organisation?: string }) {
  const { lang, say } = useStaffText()
  const [saved, setSaved] = useState<ApprovalSettings>(DEFAULT_SETTINGS)
  const [draft, setDraft] = useState<ApprovalSettings>(DEFAULT_SETTINGS)
  const [loaded, setLoaded] = useState(false)
  // The organisation's Break approval switch; null until read (or unreadable).
  const [breakApproval, setBreakApproval] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    const settings = withDefaults((await api.branchApprovals(branchId)).settings)
    setSaved(settings)
    setDraft(settings)
    setLoaded(true)
  }, [branchId])
  const { busy, error, notice, run } = useAction(load)

  useEffect(() => {
    load().catch(() => setLoaded(true))
  }, [load])
  useEffect(() => {
    api
      .counterPolicy(organisation)
      .then((r) => setBreakApproval(r.policy.breakApproval))
      .catch(() => setBreakApproval(null))
  }, [organisation])

  const toSend = settingsToSend(draft)
  const problem = approvalSettingsProblem(toSend)
  const perEmail = emailProblems(draft.emails)
  const unsaved = JSON.stringify(toSend) !== JSON.stringify(settingsToSend(saved))

  async function save() {
    if (problem) return
    await run(async () => {
      const settings = withDefaults((await api.saveBranchApprovals(branchId, toSend)).settings)
      setSaved(settings)
      setDraft(settings)
    }, say('admin.approvals.saved'))
  }

  const toggle = (k: RequestKind, on: boolean) =>
    setDraft({ ...draft, needsApproval: on ? [...draft.needsApproval, k] : draft.needsApproval.filter((x) => x !== k) })
  const setEmail = (i: number, value: string) => setDraft({ ...draft, emails: draft.emails.map((x, j) => (j === i ? value : x)) })

  const head = (
    <PageHead
      title={say('admin.section.approvals')}
      line={say('settings.approvals.line')}
      actions={
        <SaveActions
          dirty={unsaved}
          busy={busy}
          blocked={!loaded || !approvalsSavable({ busy, unsaved, problem })}
          onDiscard={() => setDraft(saved)}
          onSave={() => void save()}
        />
      }
    />
  )
  if (!loaded) return head

  return (
    <>
      {head}
      <Messages error={error} notice={notice} />

      <ListCard title={say('settings.approvals.requests')}>
        <ListTable
          label={say('settings.approvals.requests')}
          cols="minmax(0, 1fr) 160px"
          head={[say('settings.approvals.request'), <span className="appr-end">{say('admin.approvals.needsApproval')}</span>]}
        >
          {KINDS.map(({ kind, icon, tone }) => {
            const label = requestKindLabel(kind, lang)
            const on = draft.needsApproval.includes(kind)
            // Off for the organisation, a break starts at once whatever is ticked here.
            const orgOff = breakApproval === false
            return (
              <ListRow key={kind} off={orgOff}>
                <NameCell
                  tile={<IconTile icon={icon} tone={tone} size="sm" />}
                  name={label}
                  sub={orgOff ? say('settings.approvals.breakOff') : undefined}
                />
                <span role="cell" className="appr-end">
                  <Switch isSelected={on} aria-label={label} isDisabled={busy} onChange={(next) => toggle(kind, next)}>
                    <Switch.Content>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Content>
                  </Switch>
                </span>
              </ListRow>
            )
          })}
        </ListTable>
      </ListCard>

      <ListCard
        title={say('admin.approvals.emails')}
        foot={
          draft.emails.length < MAX_APPROVAL_EMAILS ? (
            <AddRow label={say('admin.approvals.addEmail')} onClick={() => setDraft({ ...draft, emails: [...draft.emails, ''] })} />
          ) : (
            <p className="appr-full">{say('settings.approvals.max')}</p>
          )
        }
      >
        <ListTable label={say('admin.approvals.emails')} cols="minmax(0, 1fr) 40px" head={[say('settings.approvals.address'), '']}>
          {draft.emails.length === 0 && <p className="appr-none">{say('settings.approvals.noEmails')}</p>}
          {draft.emails.map((email, i) => {
            const wrong = perEmail[i]
            return (
              <ListRow key={i}>
                <span role="cell" className="appr-addr">
                  <Input
                    fullWidth
                    type="email"
                    value={email}
                    placeholder={say('admin.approvals.emailPlaceholder')}
                    aria-label={say('admin.approvals.emailPlaceholder')}
                    aria-invalid={wrong ? true : undefined}
                    autoFocus={email === '' && i === draft.emails.length - 1}
                    onChange={(e) => setEmail(i, e.target.value)}
                  />
                  {wrong && <small className="appr-err">{say(wrong)}</small>}
                </span>
                <span role="cell" className="appr-end">
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    aria-label={say('common.remove')}
                    onPress={() => setDraft({ ...draft, emails: draft.emails.filter((_, j) => j !== i) })}
                  >
                    <Icon name="trash" size={16} />
                  </Button>
                </span>
              </ListRow>
            )
          })}
        </ListTable>
      </ListCard>
    </>
  )
}
